
import useDocumentHead from '@/hooks/useDocumentHead';
/**
 * Site-wide JSON-LD payload mounted in <Layout/> so every page emits the
 * Inside Panama identity blocks Google + LLMs cross-reference:
 *
 *   1. WebSite — root entity for sitelinks search box + brand graph.
 *   2. NewsMediaOrganization — formally identifies Inside Panama as an
 *      editorial newsroom (matters more than "Organization" for Google
 *      News, AI Overviews, Perplexity). Founded by two French sisters
 *      who relocated to Panama in March 2026.
 *   3. Two Person nodes (Jade + Natasha) — named, dated founders feed
 *      LLMs the E-E-A-T signal that "Inside Panama" is a real masthead
 *      with attributed authors, not an anonymous SEO farm. Identity is
 *      cross-confirmed by humans.txt, /a-propos, and the per-author
 *      pages (/auteurs/jade, /auteurs/natasha).
 *
 * Each page's SEOHead still emits its own Article / FAQPage / Product
 * payload on top — these don't compete, they reinforce.
 */
export default function GlobalSchema() {
  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://insidepanama.com/#website',
    url: 'https://insidepanama.com/',
    name: 'Inside Panama',
    alternateName: ['Inside Panama Magazine', 'InsidePanama'],
    description:
      'Magazine éditorial trilingue (FR/EN/ES) sur la vie au Panama : visa, banque, assurance, comparatifs et outils pour expatriés.',
    inLanguage: ['fr', 'en', 'es'],
    publisher: { '@id': 'https://insidepanama.com/#organization' },
    // Pas de SearchAction : la recherche du site est un overlay client (Fuse.js)
    // qui saute direct à l'article, sans URL de résultats. Déclarer une
    // sitelinks-searchbox pointant vers une URL qui ne cherche rien = schema
    // trompeur. À rajouter le jour où une page /blog?q= filtrante existe.
  };

  // Founder Person entities — referenced by @id from the organization
  // node so AI agents can resolve the masthead to actual humans with
  // documented bios on the same domain.
  const jade = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': 'https://insidepanama.com/#person-jade',
    name: 'Jade',
    givenName: 'Candice',
    familyName: 'Martinez',
    alternateName: 'Candice Martinez',
    jobTitle: 'Co-founder & editor-in-chief',
    image: 'https://insidepanama.com/images/author/jade.jpg',
    url: 'https://insidepanama.com/auteurs/jade',
    nationality: { '@type': 'Country', name: 'France' },
    homeLocation: { '@type': 'Place', name: 'Panama City, Panama' },
    knowsLanguage: ['fr', 'en', 'es'],
    knowsAbout: [
      'Friendly Nations Visa Panama',
      'Panama banking',
      'Panamanian corporate law',
      'pet relocation to Panama',
      'territorial taxation',
    ],
    worksFor: { '@id': 'https://insidepanama.com/#organization' },
    sameAs: [
      'https://www.linkedin.com/in/inside-panama',
      'https://www.instagram.com/insidepanama',
    ],
  };

  const natasha = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': 'https://insidepanama.com/#person-natasha',
    name: 'Natasha',
    givenName: 'Eva',
    familyName: 'Martinez',
    alternateName: 'Eva Martinez',
    jobTitle: 'Co-founder & editor',
    image: 'https://insidepanama.com/images/author/natasha.jpg',
    url: 'https://insidepanama.com/auteurs/natasha',
    nationality: { '@type': 'Country', name: 'France' },
    homeLocation: { '@type': 'Place', name: 'Panama City, Panama' },
    knowsLanguage: ['fr', 'en', 'es'],
    knowsAbout: [
      'Panama real estate',
      'Panama cost of living',
      'expat daily life Panama',
      'neighborhood reviews Panama City',
    ],
    worksFor: { '@id': 'https://insidepanama.com/#organization' },
    sameAs: [
      'https://www.linkedin.com/in/inside-panama',
      'https://www.instagram.com/insidepanama',
    ],
  };

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    '@id': 'https://insidepanama.com/#organization',
    name: 'Inside Panama',
    legalName: 'LAS FUNDADORAS HERMANAS S.A.',
    taxID: '155776412-2-2025',
    alternateName: ['Inside Panama Magazine', 'insidepanama.com'],
    url: 'https://insidepanama.com/',
    logo: {
      '@type': 'ImageObject',
      url: 'https://insidepanama.com/logo.png',
      width: 256,
      height: 64,
    },
    image: 'https://insidepanama.com/og-default.png',
    foundingDate: '2026-03',
    foundingLocation: {
      '@type': 'Place',
      name: 'Panama City, Panama',
    },
    founder: [
      { '@id': 'https://insidepanama.com/#person-jade' },
      { '@id': 'https://insidepanama.com/#person-natasha' },
    ],
    employee: [
      { '@id': 'https://insidepanama.com/#person-jade' },
      { '@id': 'https://insidepanama.com/#person-natasha' },
    ],
    description:
      'Trilingual (FR/EN/ES) editorial magazine on life in Panama, founded in March 2026 by two French sisters (aged 20 and 24) who relocated from Paris to Panama City with their two dogs and document the move in real time.',
    slogan: 'Le guide pratique pour vivre au Panama',
    diversityPolicy: 'https://insidepanama.com/methodologie',
    ethicsPolicy: 'https://insidepanama.com/methodologie',
    actionableFeedbackPolicy: 'https://insidepanama.com/methodologie#corrections',
    correctionsPolicy: 'https://insidepanama.com/methodologie#corrections',
    unnamedSourcesPolicy: 'https://insidepanama.com/methodologie',
    verificationFactCheckingPolicy: 'https://insidepanama.com/methodologie',
    masthead: 'https://insidepanama.com/a-propos',
    publishingPrinciples: 'https://insidepanama.com/methodologie',
    knowsAbout: [
      'Panama',
      'Friendly Nations Visa',
      'Pensionado Visa',
      'Panama City',
      'Casco Viejo',
      'Bella Vista',
      'Marbella',
      'Costa del Este',
      'El Cangrejo',
      'Banco General',
      'Tower Bank',
      'Banistmo',
      'MultiBank Panama',
      'Wise',
      'Cigna Global',
      'SafetyWing',
      'IMG Global',
      'Boquete',
      'Volcán Chiriquí',
      'Bocas del Toro',
      'San Blas Islands',
      'Panama Canal',
      'Panama territorial taxation',
      'Panamanian Sociedad Anónima',
      'Kraemer & Kraemer',
      'pet relocation to Panama',
      'expat life Panama',
      'Air France direct CDG-PTY',
      'Hospital Punta Pacífica',
      'Pacífica Salud',
    ],
    knowsLanguage: ['fr', 'en', 'es'],
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'PH Volcán Condo Village, Vía Principal, Urbanización Volcán',
      addressLocality: 'Volcán',
      addressRegion: 'Chiriquí',
      postalCode: '0426',
      addressCountry: 'PA',
    },
    areaServed: { '@type': 'Country', name: 'Panama' },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'hello@insidepanama.com',
        availableLanguage: ['French', 'English', 'Spanish'],
      },
      {
        '@type': 'ContactPoint',
        contactType: 'press',
        email: 'presse@insidepanama.com',
        availableLanguage: ['French', 'English', 'Spanish'],
      },
      {
        '@type': 'ContactPoint',
        contactType: 'partnerships',
        email: 'partenariats@insidepanama.com',
        availableLanguage: ['French', 'English'],
      },
      {
        '@type': 'ContactPoint',
        contactType: 'legal',
        email: 'legal@insidepanama.com',
        availableLanguage: ['French', 'English', 'Spanish'],
      },
    ],
    sameAs: [
      'https://www.youtube.com/@insidepanama',
      'https://www.instagram.com/insidepanama',
      'https://www.linkedin.com/company/insidepanama',
      'https://twitter.com/insidepanama',
      'https://www.threads.net/@insidepanama',
    ],
  };

  // Ces 4 schemas passaient par <Helmet>, donc ils n'atteignaient jamais le
  // <head> : les Person de Jade et Natasha (signal E-E-A-T) etaient perdus, et
  // les 2 autres n'existaient que grace aux copies statiques d'index.html.
  useDocumentHead({ jsonLd: [website, organization, jade, natasha] });
  return null;
}
